/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React from "react";
import { debounce } from "@mui/material";
import UIConstants from "../../UIConstants";
import ChangerTrackerNavigateOptions from "../Components/AppContext/ChangeTrackerNavigateOptions";
import Query from "../Queries/Query";
import AnyUtil from "./AnyUtil";
import useChangeTracker from "./useChangeTracker";

type QueryCtor<TData, TInput> = new (input: TInput, onCompleted: (data: TData) => void, additionalParameter: any) => Query<TData, TInput>;

export type QueryState<T> =
    | { loading: true, data?: T }
    | { loading: false, data: T };

type UseQueryInit<TData, TInput, TTransformedData> = {
    query: QueryCtor<TData, TInput>;
    input: TInput;
    additionalCtorParameter?: any;
    transform?: (data: TData) => TTransformedData;
    changeTrackerInit?: {
        onSave: (options: ChangerTrackerNavigateOptions) => Promise<void>;
        ignoreFields?: (keyof TTransformedData)[];
    }
};

export default function useQuery<
    TData extends {},
    TInput extends {},
    TTransformedData extends {} = TData
>({
    query: ctor,
    input,
    transform = data => data as any,
    changeTrackerInit = undefined,
    additionalCtorParameter = undefined
}: UseQueryInit<TData, TInput, TTransformedData>) {
    const [state, setState] = React.useState<QueryState<TTransformedData>>({ loading: true });
    const [changeTracker, resetChangeTracker] = useChangeTracker(
        removeIgnoredProperties(state.data),
        changeTrackerInit?.onSave);

    const map = React.useCallback(<T>(func: (data: TTransformedData) => NonNullable<T>) => {
        if (!state.loading) {
            return func(state.data);
        }
    }, [state.loading, state.data]);

    function mapOrDefault<T>(func: (data: TTransformedData) => NonNullable<T>, defaultValue: NonNullable<T>) {
        const result = map(func);
        return result !== undefined ? result : defaultValue;
    }

    function removeIgnoredProperties(data?: TTransformedData) {
        if (AnyUtil.isNullOrUndefined(data) || changeTrackerInit === undefined) {
            return {};
        }

        return Object.entries(data).reduce((acc, [key, value]) => {
            const field = key as keyof TTransformedData;
            if (!changeTrackerInit.ignoreFields?.includes(field)) {
                (acc as any)[field] = value;
            }

            return acc;
        }, {} as Partial<TTransformedData>);
    }

    function change<TField extends keyof TTransformedData>(field: TField, value: TTransformedData[TField]) {
        setState(prev => {
            if (prev.data === undefined) {
                return prev;
            }

            return {
                ...prev,
                data: {
                    ...prev.data,
                    [field]: value,
                }
            };
        });
    }

    const zeigeLoading = React.useMemo(
        () => debounce(() => setState({ loading: true }), UIConstants.zeigeLoadingNachMillis),
        [setState]);
    React.useEffect(() => {
        zeigeLoading();
    }, [zeigeLoading, JSON.stringify(input)]);

    const query = new ctor(
        input, data => {
            zeigeLoading.clear();
            const transformed = transform(data);
            setState({ loading: false, data: transformed });
            resetChangeTracker(removeIgnoredProperties(transformed))
        },
        additionalCtorParameter);

    return {
        query,
        state,
        setState,
        change,
        map,
        mapOrDefault,
        changeTracker: {
            ...changeTracker,
            reset: resetChangeTracker,
        },
    };
}